import {
  memo,
  PropsWithChildren,
  useMemo,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router-dom";

import { keepSpecificParams } from "../../../utils/searchParamHelper";
import { SELECTED_DATE_PARAM_KEY } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { UNPLANNED_SELECTED_DATE } from "../../../hooks/useUnplannedDatetimeSearchParam";
import { PlanType } from "../../../types/planning/plan.type";
import type {
  InboundPages,
  InboundTabs,
} from "../../../types/inbound/inboundView.type";
import { Tooltip } from "../Tooltip";
import { formatTrailerTitle } from "../../../pages/inbound/shared/utils";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { TABLE_SELECTION_PARAM_KEY } from "../../../hooks/useTableSelectionParam";

export const TRAILER_TITLE_PARAM = "tTitle"; // contracted due filter naming conflicts
export const TRAILER_NAME_PARAM = "tName"; // contracted due filter naming conflicts
export const SHIPMENT_TABLE_PARAM = "shipmentTable";
export const PLAN_TYPE_PARAM = "planType";
export const BUCKET_PLAN_DATE_PARAM = "bucketPlanDate";

const SHIPMENT_IDS_PARAM = "shipmentIds";

type TableLinkProps = {
  id: string;
  title: string;
  name?: string;
  inboundPage?: InboundPages;
  tab: InboundTabs;
  shipmentIds?: number[];
  iconComponent?: ReactNode;
  planType?: PlanType;
  style?: CSSProperties;
  paramsToKeep?: string[];
  selection?: string[];
  bucketPlanDate?: Date | null;
};

const TableLink = ({
  id,
  title,
  name,
  inboundPage,
  tab,
  shipmentIds,
  children,
  iconComponent,
  planType,
  style,
  paramsToKeep = [],
  selection,
  bucketPlanDate,
}: PropsWithChildren<TableLinkProps>) => {
  const [serviceCenter] = useSelectedServiceCenter();
  const [search] = useSearchParams();

  const currentSearchParams = useMemo(() => {
    const newSearchParam = keepSpecificParams(new URLSearchParams(search), [
      SELECTED_DATE_PARAM_KEY,
      UNPLANNED_SELECTED_DATE,
      ...paramsToKeep,
    ]);
    newSearchParam.set(SHIPMENT_TABLE_PARAM, "true");
    newSearchParam.set(TRAILER_TITLE_PARAM, title);
    if (name) {
      newSearchParam.set(TRAILER_NAME_PARAM, name);
    }
    if (planType) {
      newSearchParam.set(PLAN_TYPE_PARAM, planType);
    }
    if (shipmentIds) {
      newSearchParam.set(SHIPMENT_IDS_PARAM, shipmentIds.join(","));
    }
    if (selection) {
      newSearchParam.set(TABLE_SELECTION_PARAM_KEY, selection.join(","));
    }
    if (bucketPlanDate !== undefined) {
      newSearchParam.set(
        BUCKET_PLAN_DATE_PARAM,
        bucketPlanDate?.toISOString() ?? "null"
      );
    }

    return newSearchParam;
  }, [
    search,
    paramsToKeep,
    title,
    name,
    planType,
    shipmentIds,
    selection,
    bucketPlanDate,
  ]);

  const fragments = useMemo(
    () => `${inboundPage ? inboundPage + "/" : ""}${tab + "/"}`,
    [inboundPage, tab]
  );

  return (
    <Link
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontWeight: "bold",
        ...style,
      }}
      to={{
        pathname: `/operations/${serviceCenter.code}/inbound/${fragments}${id}/shipments`,
        search: currentSearchParams.toString(),
      }}
      onClick={(ev) => ev.stopPropagation()}
    >
      {iconComponent}
      <Tooltip content={formatTrailerTitle(name, title)}>{children}</Tooltip>
    </Link>
  );
};
export default memo(TableLink);
